import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { MuiModal } from "../../../../../../components";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { activitiesAndDeliverablesSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import {
  CreateActivityDefaultFormKeys,
  CreateActivityDefaultProps,
  CreateActivityGeneralFormKeys,
  CreateActivityGeneralProps,
  CreateClientActivitiesRowValue,
  EditActivityValueProps,
} from "../../modelsAndSchemas/models";
import { CreateActivityWrapper } from "./CreateActivity.style";
import { CreateActivityGeneral } from "./createActivityGeneral/CreateActivityGeneral";

import {
  useCreateClientActivityMutation,
  useCreateClientActivityUpdateMutation,
} from "../../../../../../api/services/client/clientApis";
import {
  CreateClientActivityPayload,
  ErrorModel,
} from "../../../../../../api/services/client/models";
import { useMdmSearchActivityMutation } from "../../../../../../api/services/mdmActivity/mdmActivityApis";
import { SearchMdmActivityParams } from "../../../../../../api/services/mdmActivity/models";
import { InformationBadge } from "../../../../../../common/informationBadge/informationBadge";
import { BE_ERROR_CODES } from "../../../../../../helpers/constants";
import { UnitTypesKey } from "../../../../../../helpers/constants/siteCoreKeys";
import {
  convertFormDataToLocalizeObj,
  getParsedBEErrors,
  hasKey,
  isNotEmpty,
  replaceHashWithValue,
} from "../../../../../../helpers/utils";
import { userPreferredLanguageSelector } from "../../../../../../redux/slices/appState/appStateSelector";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../redux/slices/appState/appStateSlice";
import { createClientSelector } from "../../../../../../redux/slices/client/createClientSelector";
import { ModalityType } from "../../../../../mdmActivitySearch/modelsAndSchemas/models";
import {
  CreateActivityDefaultValidationSchema,
  CreateActivityGeneralValidationSchema,
} from "../../modelsAndSchemas/ValidationSchema";
import { CancelFieldModal } from "../cancelFieldModal/CancelFieldModal";
import { CreateActivityDefault } from "./createActivityDefault/CreateActivityDefault";

interface CreateActivityProps {
  show: boolean;
  handleCloseModal: () => void;
  pageId: string;
  getListOfActivitiesApi: () => void;
  isEditActivity?: boolean;
  editActivityData?: EditActivityValueProps;
  isPendingActivity?: boolean;
  selectedActivity?: CreateClientActivitiesRowValue;
}

export const CreateActivity: FC<CreateActivityProps> = ({
  show,
  handleCloseModal,
  pageId,
  getListOfActivitiesApi,
  isEditActivity = false,
  editActivityData,
  isPendingActivity = false,
  selectedActivity,
}) => {
  const dispatch = useDispatch();
  const { id: clientId } = useSelector(createClientSelector);
  const deliveryRef = useRef<{ resetValues: (values?: string[]) => void }>(
    null,
  );
  const participantStatusRef = useRef<{
    resetValues: (values?: string[]) => void;
  }>(null);
  const userSelectedLanguage: string = useSelector(
    userPreferredLanguageSelector,
  );
  const [showCancel, setShowCancel] = useState(false);
  const [duplicateErrorMessage, setDuplicateErrorMessage] = useState<string>();
  const {
    commonLabels,
    fetchLabel,
    fetchComponentTitle,
    commonValidationMessages,
    fetchDropdownValues,
  } = useSiteCore();

  const {
    mutateAsync: saveActivityApi,
    isPending: isSavePending,
    isError: isSaveError,
    error: saveError,
    isSuccess: isSaveSuccess,
  } = useCreateClientActivityMutation();

  const {
    mutateAsync: updateActivityApi,
    isSuccess: isUpdateSuccess,
    isPending: isUpdatePending,
    isError: isUpdateError,
    error: updateError,
  } = useCreateClientActivityUpdateMutation();

  const {
    mutateAsync: getMdmSearchActivityList,
    isPending: activityTypesPending,
    data: activityTypeResult,
    isError: isActivityTypeError,
  } = useMdmSearchActivityMutation();

  useEffect(() => {
    getMdmSearchActivityList({
      ...(isEditActivity ? { id: selectedActivity?.id ?? "" } : {}),
    } as SearchMdmActivityParams);
  }, []);

  useEffect(() => {
    if ((isSaveError && saveError) || (isUpdateError && updateError)) {
      const errorObj = isSaveError ? saveError : updateError;
      const error = getParsedBEErrors(errorObj)[0];
      if (isNotEmpty(error) && hasKey(error, "errorCode")) {
        showErrorsFromBE(error);
      }
    }
  }, [isSaveError, saveError, isUpdateError, updateError]);

  const showErrorsFromBE = (error: ErrorModel) => {
    if (error.errorCode === BE_ERROR_CODES.DUPLICATE_CLIENT_SPECIFIC_ACTIVITY) {
      setDuplicateErrorMessage(
        fetchLabel(
          activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
          activitiesAndDeliverablesSiteCoreKeys.duplicateEntriesFoundInActivities,
        ),
      );
    }
  };

  const isLoading = isSavePending || isUpdatePending || activityTypesPending;

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isLoading,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isLoading]);

  useEffect(() => {
    if (isUpdateSuccess || isSaveSuccess) {
      dispatch(
        toggleToast({
          open: true,
          message: fetchLabel(
            activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
            isUpdateSuccess
              ? activitiesAndDeliverablesSiteCoreKeys.activityUpdatedMessage
              : activitiesAndDeliverablesSiteCoreKeys.activityCreatedMessage,
          ),
        }),
      );
      getListOfActivitiesApi();
      handleCloseModal();
    }
    if (isSaveError || isUpdateError || isActivityTypeError) {
      const error = getParsedBEErrors(isSaveError ? saveError : updateError);
      if (
        error[0]?.errorCode !==
        BE_ERROR_CODES.DUPLICATE_CLIENT_SPECIFIC_ACTIVITY
      ) {
        dispatch(
          toggleToast({
            open: true,
            message: commonLabels.someThingWentWrongPleaseTryAgainLater,
            type: "error",
          }),
        );
        dispatch(toggleLoader({ isVisible: false }));
      }
    }
  }, [isSaveSuccess, isSaveError, isUpdateError, isUpdateSuccess]);

  const modalityRef = useRef<{ resetValues: (values?: string[]) => void }>(
    null,
  );

  const defaultValuesGeneral = {
    [CreateActivityGeneralFormKeys.candidateStatuses]: [],
    [CreateActivityGeneralFormKeys.activityType]: "",
    [CreateActivityGeneralFormKeys.modality]: [],
    [CreateActivityGeneralFormKeys.topic]: {
      [userSelectedLanguage]: "",
    },
    [CreateActivityGeneralFormKeys.deliverables]: [],
    [CreateActivityGeneralFormKeys.activityStatus]: "",
    [CreateActivityGeneralFormKeys.triggerBilling]: false,
    [CreateActivityGeneralFormKeys.lastContactDate]: false,
    [CreateActivityGeneralFormKeys.triggerTouch]: false,
  };

  const defaultValueDefault = {
    [CreateActivityDefaultFormKeys.units]: "",
    [CreateActivityDefaultFormKeys.measurementMinute]: "",
    [CreateActivityDefaultFormKeys.measurementOther]: null,
  };

  //form for general tab
  const {
    control,
    getValues,
    setValue,
    formState: { isDirty: isDirtyGeneralForm, dirtyFields },
    reset: resetGeneralForm,
    trigger: triggerGeneralForm,
  } = useForm<CreateActivityGeneralProps>({
    mode: "all",
    defaultValues: defaultValuesGeneral,
    resolver: yupResolver(
      CreateActivityGeneralValidationSchema(
        commonValidationMessages,
        userSelectedLanguage,
      ),
    ) as any,
  });
  //form form default tab
  const {
    watch,
    resetField: resetFieldDefaultTabValue,
    control: controlDefaultTab,
    getValues: defaultTabValues,
    setValue: defaultTabSetValue,
    formState: { isDirty: isDirtyDefaultForm },
    reset: resetDefaultForm,
    trigger: triggerDefaultForm,
  } = useForm<CreateActivityDefaultProps>({
    mode: "all",
    defaultValues: defaultValueDefault,
    resolver: yupResolver(
      CreateActivityDefaultValidationSchema(commonValidationMessages),
    ),
  });

  useEffect(() => {
    resetFieldDefaultTabValue(CreateActivityDefaultFormKeys.measurementOther);
    resetFieldDefaultTabValue(CreateActivityDefaultFormKeys.measurementMinute);
  }, [watch(CreateActivityDefaultFormKeys.units)]);

  useEffect(() => {
    if (
      isNotEmpty(editActivityData) &&
      isNotEmpty(activityTypeResult?.data) &&
      isEditActivity
    ) {
      resetGeneralForm({
        ...editActivityData,
        activityType: selectedActivity?.id ?? "",
      });
      resetDefaultForm({
        ...editActivityData,
        measurementOther: editActivityData?.measurementOther ?? null,
      });

      participantStatusRef.current?.resetValues(
        editActivityData?.candidateStatuses ?? [],
      );
      deliveryRef.current?.resetValues(editActivityData?.deliverables ?? []);
      setTimeout(() => {
        modalityRef.current?.resetValues(editActivityData?.modality ?? []);
      });
    }
  }, [editActivityData, isEditActivity, activityTypeResult?.data]);

  const activityTypeWatch = useWatch({
    control,
    name: CreateActivityGeneralFormKeys.activityType,
  });

  useEffect(() => {
    const matched = activityTypeResult?.data?.find(
      (item: { id: string }) => item.id === activityTypeWatch,
    );
    if (matched?.units) {
      defaultTabSetValue(CreateActivityDefaultFormKeys.units, matched?.units, {
        shouldValidate: true,
      });
    }
  }, [activityTypeWatch, activityTypeResult?.data]);

  const onFinalSubmitClick = () => {
    const {
      activityType,
      modality: modalities,
      topic,
      activityStatus,
      candidateStatuses,
      deliverables,
      triggerBilling,
      lastContactDate,
      triggerTouch,
    } = getValues();
    const { units, measurementMinute, measurementOther } = defaultTabValues();

    const measurement =
      units === UnitTypesKey.Minutes
        ? measurementMinute
        : units === UnitTypesKey.Count || units === UnitTypesKey.Days
          ? (measurementOther as number).toString()
          : "1";

    const payload: CreateClientActivityPayload = {
      activityTypeId: activityType,
      modalities,
      localizedTopicNames: convertFormDataToLocalizeObj(topic),
      activityStatus,
      candidateStatuses,
      deliverableIds: deliverables,
      triggerBilling,
      countsTowardsLastContactDate: lastContactDate,
      triggerTouchPointSurvey: triggerTouch,
      units,
      measurement: measurement,
    };
    if (isEditActivity) {
      payload["id"] = selectedActivity?.id ?? "";
      updateActivityApi({ payload });
    } else {
      saveActivityApi({ payload, clientId });
    }
  };

  const modalityListDropdownValues = fetchDropdownValues(
    activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
    activitiesAndDeliverablesSiteCoreKeys.modalityList,
  );

  const selectedModalityList = useMemo(() => {
    if (dirtyFields?.activityType) {
      setValue(CreateActivityGeneralFormKeys.modality, [], {
        shouldValidate: false,
      });
      modalityRef.current?.resetValues([]);
    }

    if (!activityTypeWatch || !Array.isArray(activityTypeResult?.data)) {
      return [];
    }

    const selectedActivityType = activityTypeResult?.data?.find(
      (item) => item.id === activityTypeWatch,
    );
    const modalityMap = new Map(
      modalityListDropdownValues.map(({ value, label }) => [value, label]),
    );
    return (
      selectedActivityType?.modalityLst.map((v: ModalityType) => ({
        value: v.modalityName,
        label: modalityMap.get(v.modalityName) ?? v.modalityName,
      })) ?? []
    );
  }, [activityTypeWatch, activityTypeResult?.data]);

  const onConfirmClick = async () => {
    const defaultFormValid = await triggerDefaultForm();
    const isGeneralFormValid = await triggerGeneralForm();
    if (defaultFormValid && isGeneralFormValid) {
      onFinalSubmitClick();
    }
  };

  const closeModalHandler = () => setShowCancel(false);

  const cancelMainModalHandler = () => {
    if (isDirtyDefaultForm || isDirtyGeneralForm) setShowCancel(true);
    else handleCloseModal();
  };

  const cancelConfirmHandler = () => {
    setShowCancel(false);
    handleCloseModal();
  };

  const activityHeaderText = useMemo(() => {
    if (isEditActivity) {
      return fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
        activitiesAndDeliverablesSiteCoreKeys.editActivityNote,
      );
    } else {
      return fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
        activitiesAndDeliverablesSiteCoreKeys.createActivityNote,
      );
    }
  }, []);

  const activityTitle = useMemo(() => {
    if (isEditActivity) {
      return replaceHashWithValue(
        fetchLabel(
          activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
          activitiesAndDeliverablesSiteCoreKeys.editActivityPopupTitle,
        ),
        editActivityData?.activityType?.name ?? "",
      );
    } else {
      return fetchComponentTitle(
        activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
      );
    }
  }, [editActivityData]);

  return (
    <MuiModal
      open={show}
      onClose={cancelMainModalHandler}
      title={activityTitle}
      primaryButtonTitle={
        isEditActivity ? commonLabels.update : commonLabels.save
      }
      secondaryButtonTitle={commonLabels.cancel}
      onPrimaryButtonClick={onConfirmClick}
      onSecondaryButtonClick={cancelMainModalHandler}
      hasLargeContent
      footerErrorMessage={duplicateErrorMessage}
    >
      <CreateActivityWrapper data-testid="create-activity-wrapper">
        <InformationBadge message={activityHeaderText} />
        <CreateActivityGeneral
          control={control}
          pageId={pageId}
          deliveryRef={deliveryRef}
          isPendingActivity={isPendingActivity}
          manageLocaleTitle={activityTitle}
          manageLocaleInfoMessage={activityHeaderText}
          activityTypeList={activityTypeResult?.data ?? []}
          participantStatusRef={participantStatusRef}
          modalityRef={modalityRef}
          modalityList={selectedModalityList}
          handleSaveManageLocal={(manageLocals) =>
            setValue(CreateActivityGeneralFormKeys.topic, manageLocals)
          }
        >
          <CreateActivityDefault
            control={controlDefaultTab}
            pageId={pageId}
            unitValue={defaultTabValues().units}
          />
        </CreateActivityGeneral>

        {showCancel && (
          <CancelFieldModal
            showModal={showCancel}
            handleModalClose={closeModalHandler}
            handlePrimaryButtonClick={cancelConfirmHandler}
            data-testid="create-activity-cancel-modal"
          />
        )}
      </CreateActivityWrapper>
    </MuiModal>
  );
};
